import { toast } from 'sonner'
import type { Block } from '@/types/blocks'
import { useReorderBlocksMutation } from '@/features/editor/blocksApi'
import { useSaveStatusWrap } from '@/features/editor/hooks/useSaveStatusWrap'
import { computeReorderUpdates } from '@/features/editor/lib/orderUtils'
import { toBlockCreate } from '@/features/editor/lib/duplicateBlock'
import { refusedByReview } from '@/features/editor/lib/reviewGuard'
import { DRAFT_SETTLING_REFUSED } from '@/features/editor/lib/reviewLockText'
import {
  selectIsDraftSourceSettling,
  useIsDraftSourceSettling,
} from '@/features/moderation/lib/draftSourceSettling'
import { useOptionalStore } from '@/features/editor/hooks/useOptionalStore'
import { isBlockLocked, type BlocksInRun } from './useBlocksInRun'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

interface Input {
  lessonId: number
  courseId: number
  blocks: Block[]
  blocksInRun: BlocksInRun
  isBlockWritingRunActive: boolean
  isFrozen: boolean
}

/**
 * Перенумерация экранов урока (часть `useLessonBlocksActions`). Тела реордера —
 * ПОЛНЫЕ (у блока `PUT` — полная замена), собранные из кеша, поэтому у неё три
 * гейта, и каждый отсекает операцию целиком с объяснением:
 * - проверка курса (`isFrozen`);
 * - переезд на только что заведённый черновик (`useIsDraftSourceSettling`): кеш ещё
 *   живой версии, и тела затёрли бы только что сохранённый в черновик экран;
 * - прогон, пишущий в блоки. С Task 10 кеш обновляется на КАЖДЫЙ тик прогона, но само
 *   тело реордера не знает, что бэк допишет тик СПУСТЯ отправку. Для правки диапазон,
 *   задевающий известный блок, отсекаем точечно (`blocksInRun`); для картинок цели
 *   знает только сервер — `isBlockWritingRunActive` отсекает операцию ЦЕЛИКОМ. Сам блок
 *   прогона не перетащить (`BlockStrip`), но диапазон может задеть его чужим ходом —
 *   молчаливый no-op автор принял бы за сбой.
 */
export const useBlockReorder = ({
  lessonId,
  courseId,
  blocks,
  blocksInRun,
  isBlockWritingRunActive,
  isFrozen,
}: Input) => {
  const wrap = useSaveStatusWrap()
  const [reorderBlocks] = useReorderBlocksMutation()
  const isSourceSettling = useIsDraftSourceSettling(courseId)
  const store = useOptionalStore()

  const reorderList = async (list: Block[], fromIndex: number, toIndex: number) => {
    const changed = computeReorderUpdates(list, fromIndex, toIndex)
    // Проверка раньше гейта прогона: на ней заперты все блоки и грубый гейт
    // тоже истинен — без этого отказ назвал бы прогон, которого нет.
    if (changed.length === 0 || refusedByReview(isFrozen)) return
    // Состояние читается в момент вызова, а не из замыкания: перенос мог быть
    // запущен до ре-рендера, который принёс бы свежий признак переезда.
    if (store ? selectIsDraftSourceSettling(store.getState(), courseId) : isSourceSettling) {
      toast.error(DRAFT_SETTLING_REFUSED)
      return
    }
    if (isBlockWritingRunActive || changed.some((u) => isBlockLocked(blocksInRun, u.item.id))) {
      toast.error(messagesNow(messages).reorderRefusedAi)
      return
    }
    const updates = changed.map((u) => ({ id: u.item.id, body: toBlockCreate(u.item, u.order) }))
    await wrap(
      reorderBlocks({ lessonId, updates }).unwrap(),
      messagesNow(messages).reorderBlocksFailed,
    )
  }

  const handleReorder = (fromIndex: number, toIndex: number) =>
    reorderList(blocks, fromIndex, toIndex)

  return { handleReorder, reorderList, isSourceSettling }
}
